<template>
  <div class="breath-trainer container">
    <h1>呼吸训练器</h1>
    
    <div class="difficulty-selector card">
      <h2>请选择训练等级</h2>
      <div class="difficulty-options">
        <button 
          v-for="level in difficultyLevels" 
          :key="level.name"
          :class="{ active: selectedLevel === level }"
          @click="selectLevel(level)"
        >
          {{ level.emoji }} {{ level.name }}
        </button>
      </div>
    </div>

    <div class="training-status card" v-if="selectedLevel">
      <div class="current-phase">
        <h3>当前阶段：{{ currentPhase }}</h3>
        <div class="countdown">{{ countdown }} 秒</div>
        <div class="instruction" v-if="isTraining">
          {{ getCurrentInstruction() }}
        </div>
      </div>
      
      <div class="time-slider" v-if="selectedLevel.name === '挑战' && !isTraining">
        <label for="duration-slider">呼吸时长：{{ selectedLevel.duration }} 秒</label>
        <input 
          type="range" 
          id="duration-slider"
          v-model.number="selectedLevel.duration"
          :min="selectedLevel.minDuration"
          :max="selectedLevel.maxDuration"
          step="1"
        >
      </div>
      
      <div class="rounds-info" v-if="isTraining">
        <p>已完成 {{ completedRounds }}/{{ selectedLevel.maxRounds }} 轮</p>
      </div>

      <div class="disclaimer" v-if="!isTraining">
        ⚠️ 屏息训练不适用于青光眼、心血管疾病、孕妇及未成年人。如有不适请立即停止并正常呼吸。
      </div>

      <div class="controls">
        <button 
          v-if="!isTraining" 
          @click="startTraining"
          class="start-btn"
        >
          开始训练
        </button>
        <button 
          v-else 
          @click="resetTraining"
          class="reset-btn"
        >
          重新开始
        </button>
        <button 
          @click="toggleMute"
          class="sound-btn"
          :class="{ 'muted': isMuted }"
        >
          {{ isMuted ? '🔇' : '🔊' }}
        </button>
      </div>

      <div class="stats" v-if="completedRounds > 0">
        <p>今日完成 {{ completedRounds }} 轮训练</p>
      </div>

      <div class="encouragement" v-if="showEncouragement">
        {{ currentEncouragement }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BreathTrainer',
  data() {
    return {
      difficultyLevels: [
        {
          name: '初阶',
          emoji: '🔰',
          duration: 4,
          maxRounds: 5,
          instructions: {
            inhale: '用鼻子缓慢吸气,感觉腹部轻轻鼓起',
            hold: '保持气息,自然舒适,放松肩颈',
            exhale: '用嘴巴缓缓呼气,像叹气般自然',
            holdAfter: '保持空肺,感受身体的放松'
          }
        },
        {
          name: '中阶',
          emoji: '⚙️',
          duration: 6,
          maxRounds: 6,
          instructions: {
            inhale: '深长吸气,腹部与胸廓协同鼓起',
            hold: '稳定屏息,感受气息在体内流转',
            exhale: '完全呼出,想象压力随气息散去',
            holdAfter: '空肺停留,内心趋于平静'
          }
        },
        {
          name: '高阶',
          emoji: '🔺',
          duration: 8,
          maxRounds: 6,
          instructions: {
            inhale: '深度吸气,尽可能扩展肺活量',
            hold: '稳定屏息,锻炼专注与耐力',
            exhale: '彻底呼气,排空肺内全部余气',
            holdAfter: '空肺状态,保持觉察与从容'
          }
        },
        {
          name: '挑战',
          emoji: '🏆',
          duration: 10,
          maxRounds: 15,
          minDuration: 8,
          maxDuration: 12,
          instructions: {
            inhale: '平稳吸气,气息绵长不断',
            hold: '舒适屏息,稳定不紧张',
            exhale: '缓慢呼气,均匀彻底',
            holdAfter: '空肺放松,感受内在宁静'
          }
        }
      ],
      selectedLevel: null,
      isTraining: false,
      currentPhase: '',
      countdown: 0,
      completedRounds: 0,
      showEncouragement: false,
      currentEncouragement: '',
      timer: null,
      phases: ['吸气', '屏息', '呼气', '屏息'],
      currentPhaseIndex: 0,
      encouragements: [
        '做得好！呼吸越稳，内心越强大。',
        '你的肺活量正在一点点进步。',
        '继续保持，专注当下每一口气。',
        '呼吸是连接身心的桥梁。',
        '每一次呼吸都是新的开始。'
      ],
      sounds: {
        hold: new Audio('/sounds/hold.mp3')
      },
      isMuted: false
    }
  },
  created() {
    this.selectedLevel = this.difficultyLevels[0]
  },
  watch: {
    currentPhase: {
      immediate: true,
      handler(newPhase) {
        this.updateDocumentTitle()
      }
    },
    countdown: {
      immediate: true,
      handler() {
        this.updateDocumentTitle()
      }
    }
  },
  methods: {
    updateDocumentTitle() {
      if (this.isTraining) {
        document.title = `${this.currentPhase} ${this.countdown}秒 - 呼吸训练`
      } else {
        document.title = '呼吸训练'
      }
    },
    toggleMute() {
      this.isMuted = !this.isMuted
      Object.values(this.sounds).forEach(sound => {
        sound.muted = this.isMuted
      })
    },
    playSound(soundName) {
      if (this.isMuted) return
      const sound = this.sounds[soundName]
      if (sound) {
        sound.currentTime = 0
        sound.play().catch(error => {
          console.warn('音效播放失败:', error)
        })
      }
    },
    selectLevel(level) {
      this.selectedLevel = level
      this.resetTraining()
    },
    startTraining() {
      if (!this.selectedLevel) return
      // 每次开始训练时清零已完成轮数,避免上次跑满后再次启动只跑 1 轮就停
      this.completedRounds = 0
      this.isTraining = true
      this.currentPhaseIndex = 0
      this.startPhase()
    },
    resetTraining() {
      this.isTraining = false
      this.currentPhaseIndex = 0
      this.countdown = 0
      this.currentPhase = ''
      this.showEncouragement = false
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    startPhase() {
      this.currentPhase = this.phases[this.currentPhaseIndex]
      // 4 阶段共用同一 duration,保持方块呼吸的对称性
      this.countdown = this.selectedLevel.duration

      this.playSound('hold')

      if (this.timer) {
        clearInterval(this.timer)
      }

      const startTime = Date.now()
      const duration = this.selectedLevel.duration * 1000

      this.timer = setInterval(() => {
        const elapsedTime = Date.now() - startTime
        const remainingTime = Math.max(0, duration - elapsedTime)
        this.countdown = Math.ceil(remainingTime / 1000)
        
        if (remainingTime <= 0) {
          clearInterval(this.timer)
          this.currentPhaseIndex++
          
          if (this.currentPhaseIndex >= this.phases.length) {
            this.completedRounds++
            this.currentPhaseIndex = 0

            if (this.completedRounds >= this.selectedLevel.maxRounds) {
              // 训练达标,进入收尾阶段,避免突然停止屏息造成不适
              this.runCooldown()
              return
            }

            this.showEncouragement = true
            this.currentEncouragement = this.encouragements[Math.floor(Math.random() * this.encouragements.length)]
            setTimeout(() => {
              this.showEncouragement = false
            }, 3000)
          }
          
          if (this.isTraining) {
            this.startPhase()
          }
        }
      }, 16)
    },
    getCurrentInstruction() {
      if (!this.selectedLevel) return ''
      const phaseMap = {
        '吸气': 'inhale',
        '屏息': this.currentPhaseIndex === 1 ? 'hold' : 'holdAfter',
        '呼气': 'exhale'
      }
      return this.selectedLevel.instructions[phaseMap[this.currentPhase]]
    },
    runCooldown() {
      // 收尾阶段:让身体从屏息状态平稳过渡到自然呼吸
      this.currentPhase = '收尾'
      this.countdown = 10
      this.playSound('hold')

      if (this.timer) {
        clearInterval(this.timer)
      }

      const startTime = Date.now()
      const duration = 10 * 1000

      this.timer = setInterval(() => {
        const elapsedTime = Date.now() - startTime
        const remainingTime = Math.max(0, duration - elapsedTime)
        this.countdown = Math.ceil(remainingTime / 1000)

        if (remainingTime <= 0) {
          clearInterval(this.timer)
          this.isTraining = false
          this.showEncouragement = true
          this.currentEncouragement = '训练完成！请自然呼吸片刻再起身活动'
          setTimeout(() => {
            this.showEncouragement = false
          }, 4000)
        }
      }, 16)
    }
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  }
}
</script>

<style scoped>
.breath-trainer {
  padding-bottom: 5rem;
}

.difficulty-selector {
  margin-bottom: 1.5rem;
}

.difficulty-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.8rem;
  margin-top: 1rem;
}

button {
  width: 100%;
  min-height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 1rem;
}

.training-status {
  position: relative;
}

.current-phase {
  text-align: center;
  margin-bottom: 1.5rem;
}

.countdown {
  font-size: 4rem;
  font-weight: bold;
  color: #4CAF50;
  margin: 1.5rem 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.instruction {
  background-color: #f8f9fa;
  border-radius: 1rem;
  padding: 1rem;
  margin: 1rem 0;
  color: #2c3e50;
  font-size: 1rem;
  line-height: 1.6;
  text-align: left;
}

.rounds-info {
  font-size: 1.1rem;
  color: #666;
  margin: 1rem 0;
}

.controls {
  display: flex;
  gap: 1rem;
  margin: 1.5rem 0;
}

.start-btn, .reset-btn {
  flex: 1;
  min-height: 3.5rem;
  font-size: 1.1rem;
  font-weight: 600;
}

.start-btn {
  background-color: #4CAF50;
  color: white;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.reset-btn {
  background-color: #f44336;
  color: white;
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
}

.sound-btn {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background-color: #4CAF50;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 100;
}

.sound-btn.muted {
  background-color: #f44336;
}

.sound-btn:active {
  transform: scale(0.95);
}

.stats {
  text-align: center;
  color: #666;
  margin: 1rem 0;
  font-size: 1rem;
}

.disclaimer {
  background-color: #fff3e0;
  border-left: 4px solid #ff9800;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  margin: 1rem 0;
  color: #5d4037;
  font-size: 0.85rem;
  line-height: 1.5;
}

.encouragement {
  background-color: #e8f5e9;
  border-radius: 1rem;
  padding: 1rem;
  margin: 1rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
  text-align: center;
  animation: fadeIn 0.5s ease;
}

@media screen and (max-width: 480px) {
  .countdown {
    font-size: 3rem;
  }

  .instruction {
    font-size: 0.9rem;
  }

  .start-btn, .reset-btn {
    font-size: 1rem;
  }
}

.time-slider {
  margin: 1.5rem 0;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 1rem;
}

.time-slider label {
  display: block;
  margin-bottom: 0.5rem;
  color: #2c3e50;
  font-size: 1rem;
}

.time-slider input[type="range"] {
  width: 100%;
  height: 8px;
  -webkit-appearance: none;
  background: #e0e0e0;
  border-radius: 4px;
  outline: none;
}

.time-slider input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  background: #4CAF50;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s;
}

.time-slider input[type="range"]::-webkit-slider-thumb:hover {
  background: #45a049;
}

.time-slider input[type="range"]::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: #4CAF50;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s;
  border: none;
}

.time-slider input[type="range"]::-moz-range-thumb:hover {
  background: #45a049;
}
</style> 